/**
 * React Query hooks for appointments
 */
import { useQuery, useMutation, useQueryClient, UseQueryOptions } from '@tanstack/react-query';
import {
  getAppointments,
  getAppointment,
  createAppointment,
  updateAppointment,
  cancelAppointment,
  Appointment,
  AppointmentCreate,
  AppointmentUpdate,
  AppointmentFilters
} from '../api/appointments';
import { toast } from 'sonner';

// ==================== Query Keys ====================

export const appointmentKeys = {
  all: ['appointments'] as const,
  lists: () => [...appointmentKeys.all, 'list'] as const,
  list: (filters?: AppointmentFilters) => [...appointmentKeys.lists(), filters] as const,
  details: () => [...appointmentKeys.all, 'detail'] as const,
  detail: (id: number) => [...appointmentKeys.details(), id] as const,
};

// ==================== Hooks ====================

/**
 * Hook to fetch appointments with filters
 */
export function useAppointments(
  filters?: AppointmentFilters,
  options?: Omit<UseQueryOptions<Appointment[]>, 'queryKey' | 'queryFn'>
) {
  return useQuery({
    queryKey: appointmentKeys.list(filters),
    queryFn: () => getAppointments(filters),
    staleTime: 2 * 60 * 1000, // 2 minutes
    ...options
  });
}

/**
 * Hook to fetch a single appointment
 */
export function useAppointment(
  id: number,
  options?: Omit<UseQueryOptions<Appointment>, 'queryKey' | 'queryFn'>
) {
  return useQuery({
    queryKey: appointmentKeys.detail(id),
    queryFn: () => getAppointment(id),
    enabled: !!id,
    staleTime: 2 * 60 * 1000,
    ...options
  });
}

/**
 * Hook to create an appointment
 */
export function useCreateAppointment() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: AppointmentCreate) => createAppointment(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: appointmentKeys.lists() });
      toast.success('Appointment scheduled successfully');
    },
    onError: (error: any) => {
      toast.error(error.message || 'Failed to schedule appointment');
    }
  });
}

/**
 * Hook to update an appointment
 */
export function useUpdateAppointment() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, data }: { id: number; data: AppointmentUpdate }) =>
      updateAppointment(id, data),
    onSuccess: (_, { id }) => {
      queryClient.invalidateQueries({ queryKey: appointmentKeys.lists() });
      queryClient.invalidateQueries({ queryKey: appointmentKeys.detail(id) });
      toast.success('Appointment updated successfully');
    },
    onError: (error: any) => {
      toast.error(error.message || 'Failed to update appointment');
    }
  });
}

/**
 * Hook to cancel an appointment
 */
export function useCancelAppointment() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: number) => cancelAppointment(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: appointmentKeys.lists() });
      toast.success('Appointment cancelled');
    },
    onError: (error: any) => {
      toast.error(error.message || 'Failed to cancel appointment');
    }
  });
}